import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Sidebar from "../../../components/Layout/Sidebar";
import Header from "../../../components/Layout/Header";
import Footer from "../../../components/Layout/Footer";

import Swal from "sweetalert2";

import {
  getData, addData, updateData,
    getDataBySubdomain,
} from "../../../services/onsite/onsiteIntroductionSpeechServices";

import { getConferenceData } from "../../../services/onsite/onsiteConferenceEventServices";

import {
  addData as addVersion,
  updateData as updateVersion,
  deleteData as deleteVersion,
} from "../../../services/event/presentations/presentationVersionService";

import {
  addData as addComment,
  updateData as updateComment,
  deleteData as deleteComment,
} from "../../../services/event/presentations/presentationCommentService";


// import "react-quill/dist/quill.snow.css";
import { formats, isQuillContentEmpty, modules, applyQuillTooltips } from "../../../utils/quill_helper";
import RichEditorWithFeatures from "../../../components/presentations/RichEditorWithFeatures";
import RichEditorSkeleton from "../../components/skeleton/RichEditorSkeleton";
import { Section,DayChip ,InfoStrip} from "../../../components/UIComponents";


/* ─── Current user from auth/localStorage ──────────────────────── */
const CURRENT_USER = {
  name: localStorage.getItem("sub_domain") || "Admin",
  role: localStorage.getItem("role")    || "admin",
};

/* ══════════════════════════════════════════════════════════════════
   versionsApi — backed by presentationVersionService
   Uses the same POST-based definePost pattern as all other controllers:
     Case 2: POST { presentation_uuid, field }        → filtered list
     Case 3: POST { id: "", ...fields }               → create snapshot
══════════════════════════════════════════════════════════════════ */
const versionsApi = {
  // Fetch all versions for a presentation + field  (Case 2)
  list: async (uuid, field,speech_type='introduction') => {
    try {
      const res = await addVersion({ presentation_uuid: uuid, field, speech_type });
      return Array.isArray(res?.data) ? res.data : [];
    } catch { return []; }
  },

  // Create a new version snapshot  (Case 3)
  save: async (uuid, field, content, savedBy) => {
    try {
      const res = await addVersion({
        id:                "",
        presentation_uuid: uuid,
        field,
        content,
        saved_by:          savedBy,
        speech_type:       'introduction',
      });
      return res?.data || null;
    } catch { return null; }
  },
};

/* ══════════════════════════════════════════════════════════════════
   commentsApi — backed by presentationCommentService
   Uses the same POST-based definePost pattern:
     Case 2: POST { presentation_uuid, field }        → filtered list
     Case 3: POST { id: "", ...fields }               → create comment
     PUT    { id, resolved: true }                    → resolve
     DELETE { id }                                    → delete
══════════════════════════════════════════════════════════════════ */
const commentsApi = {
  // Fetch all comments for a presentation + field  (Case 2)
  list: async (uuid, field,speech_type='introduction') => {
    try {
      const res = await addComment({ presentation_uuid: uuid, field, speech_type });
      return Array.isArray(res?.data) ? res.data : [];
    } catch { return []; }
  },

  // Create a new comment or instruction  (Case 3)
  add: async (uuid, field, comment) => {
    try {
      const res = await addComment({
        id:                "",
        presentation_uuid: uuid,
        field,
        ...comment,        // { author, role, text, type }
        speech_type:        'introduction',
      });
      return res?.data || null;
    } catch { return null; }
  },

  // Resolve a comment — PUT { id, resolved: true }
  resolve: async (id) => {
    try {
      const res = await updateComment({ id, resolved: true });
      return res?.data || null;
    } catch { return null; }
  },

  // Delete a comment — DELETE { id }
  remove: async (id) => {
    try {
      const res = await deleteComment({ id });
      return res?.data || null;
    } catch { return null; }
  },
};

/* ─── react-select / quill shared styles injected once ─── */
const quillStyles = `
  .ql-toolbar.ql-snow {
    border-radius: 0.5rem 0.5rem 0 0 !important;
    border: 1.5px solid #dee2e6 !important;
    background: #f8f9fc;
    padding: 8px 12px;
  }
  .ql-container.ql-snow {
    border-radius: 0 0 0.5rem 0.5rem !important;
    border: 1.5px solid #dee2e6 !important;
    border-top: none !important;
    font-size: 14px;
    min-height: 180px;
  }
  .quill-error .ql-toolbar.ql-snow {
    border-color: #dc3545 !important;
  }
  .quill-error .ql-container.ql-snow {
    border-color: #dc3545 !important;
    background: #fff5f5;
  }
  .ql-editor { min-height: 160px; }
  .ql-editor.ql-blank::before { color: #adb5bd; font-style: normal; }
`;

function EventIntroductionSpeech() {
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const navigate = useNavigate();
  const speechType = "introduction_speech";
  const subDomain = localStorage.getItem("sub_domain");
  const quillRef = useRef(null);

  const [form, setForm] = useState({
    id: "", series: "", uuid: "", no_of_days: "",
    sub_domain: subDomain || "",
    primary_sub_domain: "", secondary_sub_domain: "", short_name: "", conference_type: "", 
    day_one: "", day_two: "",
    permission: "",
  });

  /* ── Quill tooltip ── */
  useEffect(() => {
    const t = setTimeout(() => applyQuillTooltips(quillRef), 100);
    return () => clearTimeout(t);
  }, [form?.uuid]);

  useEffect(() => { applyQuillTooltips(quillRef); }, []);
  const location = useLocation();
  
  const fetchAll = async () => {
    try {
      setLoading(true);
      const [confData,speechData] = await Promise.all([
        getConferenceData({ sub_domain: subDomain }),
        getDataBySubdomain({ sub_domain: subDomain }),
      ]);

     const conf = confData?.data || confData;
     const r = speechData?.data || speechData || {};

      setForm((p) => ({
          ...p,
          uuid: conf?.uuid ??  p.uuid,
          series: conf?.series ??  p.series,
          conference_title: conf?.conference_title || "",
          from_date: conf?.from_date || "",
          to_date: conf?.to_date || "",
          venue: conf?.venue ?? p.venue,
          no_of_days: conf?.no_of_days ? Number(conf.no_of_days) : 0,

          id: r.id?? p.id,
          short_name: conf?.short_name ?? p.short_name,
          conference_type: conf?.conference_type ?? p.conference_type,
          primary_sub_domain: conf?.primary_sub_domain ?? p.primary_sub_domain,
          secondary_sub_domain: conf?.secondary_sub_domain ?? p.secondary_sub_domain,
          day_one: r.day_one?? p.day_one,
          day_two:  r.day_two?? p.day_two,
          permission: r.permission?.trim()?.toLowerCase() || "granted",
        }));
    } catch (e) {
      console.error(e);
    }
    finally{
      setLoading(false);
    }
  };

  
  useEffect(() => {
   if (subDomain) {
     fetchAll();
   }
 }, [subDomain]);

 // ── Re-fetch when navigated back with refetch flag
 useEffect(() => {
   if (location.state?.refetch && subDomain) {
     fetchAll();
 
     // ✅ Clear the flag so it doesn't re-trigger on internal re-renders
     window.history.replaceState({}, "");
   }
 }, [location.state]);
 


  /* ── Clear day two if only 1 day ── */
  useEffect(() => {
    if (Number(form.no_of_days) === 1) setForm((p) => ({ ...p, day_two: "" }));
  }, [form.no_of_days]);

    const handleRichChange = (field, value) => {
      setErrors((p) => ({
        ...p,
        [field]: isQuillContentEmpty(value) ? `${field.replace("_", " ")} is required` : "",
      }));
      setForm((p) => ({ ...p, [field]: value }));
    };
  

  const validateForm = () => {
    const e = {};
    if (isQuillContentEmpty(form.day_one)) e.day_one = "Day 1 speech content is required";
    if (Number(form.no_of_days) >= 2 && isQuillContentEmpty(form.day_two)) e.day_two = "Day 2 speech content is required";
    setErrors(e);
    return !Object.keys(e).length;
  };

const originalFormRef = useRef({});

// Call this when you populate the form (on edit load)
// e.g. inside your fetchData or useEffect where you setForm
useEffect(() => {
  if (form.uuid) {
    originalFormRef.current = {
      day_one: form.day_one,
      day_two: form.day_two,
    };
  }
}, [form.uuid]);

const handleSubmit = async () => {
  if (!validateForm()) return;

  setBtnLoading(true);

  try {
    const isEdit = !!form.id;

    const response = isEdit
      ? await updateData(form)
      : await addData(form);

    if (response.status === 200 || response.status === 201) {
      const presentationId = form.uuid;

      if (presentationId && presentationId !== "new") {

        const original = originalFormRef.current;

        // ✅ Only save version if day_one actually changed
        const day1Changed = form.day_one !== original.day_one;
        if (day1Changed && form.day_one && form.day_one !== "<p><br></p>") {
          await versionsApi.save(presentationId, "day_one", form.day_one, CURRENT_USER.name);
        }

        // ✅ Only save version if day_two actually changed
        const day2Changed = form.day_two !== original.day_two;
        if (Number(form.no_of_days) >= 2 && day2Changed && form.day_two && form.day_two !== "<p><br></p>") {
          await versionsApi.save(presentationId, "day_two", form.day_two, CURRENT_USER.name);
        }

        // ✅ Update ref to latest saved values
        originalFormRef.current = {
          day_one: form.day_one,
          day_two: form.day_two,
        };
      }

      Swal.fire({
        icon: "success",
        title: "Success",
        text: isEdit ? "Introduction Speech updated successfully" : "Introduction Speech created successfully",
        timer: 2000,
        showConfirmButton: false,
      });
    }

    await fetchAll();

  } catch (err) {
    console.error("Submit error:", err);
    Swal.fire({ icon: "error", title: "Error", text: "Something went wrong" });
  } finally {
    setBtnLoading(false);
  }
};
  

  const pdfModal = (dayType, row) => {
    navigate("/introductionspeech-view", {
      state: { uuid: row.uuid, day: dayType, content: row.content, returnUrl: "/eventIntroductionSpeech" },
    });
  };

  const noOfDays = form.no_of_days ? parseInt(form.no_of_days, 10) : null;

  const hasDay1 = noOfDays !== null && noOfDays >= 1;
  const hasDay2 = noOfDays !== null && noOfDays >= 2;

  const isRestricted = form.permission?.toLowerCase() === "restricted";

  return (
    <>
      <style>{quillStyles}</style>
      <div className="d-flex">
        <Sidebar />
        <div className="main-content w-100">
          <Header />
          {loading === true ? <RichEditorSkeleton /> : (
            <>
              <div className="container-fluid p-4">

                {/* ── Page Header ── */}
                <div className="d-flex align-items-start justify-content-between mb-4">
                  <div>
                    <nav style={{ fontSize: 13, color: "#8a90a2", marginBottom: 4 }}>
                      <span>Onsite Management</span>
                      <span className="mx-2" style={{ color: "#c5c9d6" }}>›</span>
                      <span style={{ color: "#1a1d2e", fontWeight: 600 }}>Introduction Speech</span>
                    </nav>
                    <div className="d-flex align-items-center gap-2">
                      <h4 className="mb-0 fw-bold" style={{ color: "#1a1d2e", letterSpacing: "-0.3px" }}>
                        Introduction Speech
                      </h4>
                      {noOfDays > 0 && (
                        <span className="badge rounded-pill px-3 py-2"
                          style={{ fontSize: 11, background: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7", fontWeight: 600 }}>
                          {noOfDays}-Day Event
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Conference Info Strip ── */}
                {(form?.uuid || form?.series || form?.sub_domain) && (
                  <InfoStrip items={[
                    { label: "UUID", value: form.uuid, color: "#4f6ef7" },
                    { label: "Series", value: form.series },
                    { label: "Conference Title", value: form.conference_title },
                    { label: "From Date", value: form.from_date },
                    { label: "To Date", value: form.to_date },
                    { label: "Conference Type", value: form.conference_type },
                    { label: "Primary Subdomain", value: form.primary_sub_domain },
                    form.secondary_sub_domain && { label: "Secondary Subdomain", value: form.secondary_sub_domain },
                    { label: "Short Name", value: form.short_name },
                    { label: "No. of Days", value: form.no_of_days, color: "#6c5ce7" },
                  ]} />
                )}
                {/* ── Permission Control ── */}
                  <div className="d-flex align-items-center gap-3 mb-4 px-1">
                    {isRestricted && (
                      <span
                        className="badge rounded-pill px-3 py-2"
                        style={{
                          fontSize: 11,
                          background: "#fff5f5",
                          color: "#dc3545",
                          border: "1px solid #f5c6cb",
                          fontWeight: 600,
                        }}
                      >
                        Read-only mode — Editing is disabled
                      </span>
                    )}
                  </div>

                {/* ── Main Card ── */}
                <div className="card border-0 shadow-sm" style={{ borderRadius: 16, overflow: "hidden" }}>

                  {/* Card Header */}
                  <div className="card-header border-0 py-3 px-4"
                    style={{ background: "linear-gradient(135deg,#1a1d2e 0%,#2d3250 100%)" }}>
                    <div className="d-flex align-items-center gap-3">
                      <div className="d-flex align-items-center justify-content-center rounded-2"
                        style={{ width: 40, height: 40, background: "rgba(255,255,255,0.12)", color: "#fff", fontSize: 18 }}>
                        🎤
                      </div>
                      <div>
                        <div className="fw-semibold text-white" style={{ fontSize: 15 }}>Introduction Speech Editor</div>
                        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
                          Compose the opening speech for each conference day
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card-body p-4">

                    {/* ── Day 1 ── */}
                    {hasDay1 && (
                      <div className="mb-4">
                        <Section
                          icon="1️⃣"
                          title={<span>Day 1 Introduction Speech <DayChip day={1} /></span>}
                          subtitle="Opening speech for the first day of the conference"
                          action={
                            form?.uuid && (
                              <button
                                type="button"
                                className="btn btn-sm d-flex align-items-center gap-1 px-3"
                                style={{ borderRadius: 8, fontSize: 12, fontWeight: 600, border: "1.5px solid #c7d1fc", background: "#f0f2fe", color: "#4f6ef7" }}
                                onClick={() => pdfModal("day_one", { type: { speechType }, uuid: form.uuid, content: form.day_one })}
                              >
                                👁 Preview Day 1
                              </button>
                            )
                          }
                        />
                        <div>
                          
                          <RichEditorWithFeatures
                            field="day_one"
                            label="Day 1"
                            value={form.day_one}
                            onChange={(v) => handleRichChange("day_one", v)}
                            error={errors.day_one}
                            currentUser={CURRENT_USER}
                            presentationId={form?.uuid || "new"}
                            required
                            readOnly={subDomain === form?.secondary_sub_domain ? true : false} // Only allow editing if user's subdomain matches primary
                            versionsApi={versionsApi}
                            commentsApi={commentsApi}
                            type='edit'
                            isRestricted={isRestricted}
                                        />
                        </div>
                      </div>
                    )}

                    {/* ── Divider between days ── */}
                    {hasDay1 && hasDay2 && <hr className="my-2" style={{ borderColor: "#f0f2f7" }} />}

                    {/* ── Day 2 ── */}
                    {hasDay2 && (
                      <div className={hasDay1 ? "mt-4" : ""}>
                        <Section
                          icon="2️⃣"
                          title={<span>Day 2 Introduction Speech <DayChip day={2} /></span>}
                          subtitle="Opening speech for the second day of the conference"
                          action={
                            form?.uuid && form.day_two && (
                              <button
                                type="button"
                                className="btn btn-sm d-flex align-items-center gap-1 px-3"
                                style={{ borderRadius: 8, fontSize: 12, fontWeight: 600, border: "1.5px solid #c7d1fc", background: "#f0f2fe", color: "#4f6ef7" }}
                                onClick={() => pdfModal("day_two", { type: { speechType }, uuid: form?.uuid, content: form.day_two })}
                              >
                                👁 Preview Day 2
                              </button>
                            )
                          }
                        />
                        <RichEditorWithFeatures
                            field="day_two"
                            label="Day 2"
                            value={form.day_two}
                            onChange={(v) => handleRichChange("day_two", v)}
                            error={errors.day_two}
                            currentUser={CURRENT_USER}
                            presentationId={form.uuid || "new"}
                            required
                            readOnly={subDomain !== form.primary_sub_domain}
                            versionsApi={versionsApi}
                            commentsApi={commentsApi}
                            type='edit'
                            isRestricted={isRestricted}
                                        />
                      </div>
                    )}
                    {noOfDays === null ? null : !hasDay1 && (
                    <div className="text-center py-5">
                      <p>No conference data found</p>
                    </div>
                    )}

                    {/* ── Actions ── */}
                    {hasDay1 && (
                      <>
                        <hr className="mt-4 mb-3" style={{ borderColor: "#f0f2f7" }} />
                        <div className="d-flex align-items-center justify-content-between">
                          <div style={{ fontSize: 12, color: "#8a90a2" }}>
                            {form.id ? "💾 Updating existing record" : "✚ Creating new record"}
                          </div>
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-outline-secondary px-4"
                              style={{ borderRadius: "0.5rem", fontWeight: 600, fontSize: 14 }}
                              onClick={() => navigate("/eventIntroductionSpeech")}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn px-4"
                              style={{
                                borderRadius: "0.5rem", fontWeight: 600, fontSize: 14, border: "none",
                                background: loading  || isRestricted? "#c7d0fb" : "linear-gradient(135deg,#4f6ef7,#6c5ce7)",
                                color: "#fff", boxShadow: loading || isRestricted ? "none" : "0 4px 14px rgba(79,110,247,.35)",
                                cursor: loading || isRestricted? "not-allowed" : "pointer",
                              }}
                              onClick={handleSubmit}
                              disabled={btnLoading ||isRestricted }
                            >
                              {btnLoading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Saving...
                              </>
                            ) : isRestricted ? (
                              "🔒 Restricted"
                            ) : (
                              "✔ Save Speech"
                            )}

                            </button>
                          </div>
                        </div>
                      </>
                    )}

                  </div>
                </div>
              </div>
              <Footer />
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default EventIntroductionSpeech;